import React, { type PropsWithChildren, useContext } from "react";
import useSWRImmutable from "swr/immutable";
import { useAuthContext } from "../auth/context";
import { fetchWork, type Work } from "../work/work";

export type WorkContext = {
  work: Work | undefined;
};

export const workContext = React.createContext<WorkContext | undefined>(
  undefined,
);

export type WorkContextProviderProps = {
  workId?: string;
};

export function WorkContextProvider(
  props: PropsWithChildren<WorkContextProviderProps>,
) {
  const authContext = useAuthContext();

  const { data, error, isLoading } = useSWRImmutable(
    // https://swr.vercel.app/docs/conditional-fetching#conditional
    () => props.workId,
    (workId: string) => fetchWork(workId, authContext.authorizationHeader),
    {
      shouldRetryOnError: false,
    },
  );

  if (isLoading) {
    return <></>;
  } else if (error) {
    throw new Error(`Error fetching work entity: ${error}`);
  }

  return (
    <workContext.Provider value={{ work: data }}>
      {props.children}
    </workContext.Provider>
  );
}

export function useWorkContext() {
  const context = useContext(workContext);
  if (!context) {
    throw new Error("useWorkContext must be used with WorkContextProvider");
  }
  return context;
}
